// router/index.js
import { createRouter, createWebHistory } from 'vue-router'
import CustomerPortal from '../views/CustomerPortal.vue'
import CartView from '../views/CartView.vue'
import CartSearchPage from '../components/CartSearchPage.vue'
import CartStep from '../components/CartStep.vue'
import CartPaymentScreen from '../components/CartPaymentScreen.vue'
import FilingsDetail from '../components/CustomerPortal/FilingsDetail.vue'
import { useFilingDetailStore } from '../stores/filingDetailStore'
import { unauthenticatedCartRoutes, authenticatedCartRoutes } from './cartRoutes'
import { useUserStore } from '../stores/userStore'


// Async guard function
const requireEditableFiling = async (to, from, next) => {
  // If we're already on FilingsDetail, don't redirect again
  if (to.name === 'FilingsDetail') {
    return next()
  }

  const store = useFilingDetailStore()

  try {
    await store.getBoiReportData(to.params.filingId, to.params.teamSlug)
    if (store.isFilingEditable) {
      return next()
    } else {
      return next({
        name: 'FilingsDetail',
        params: { teamSlug: to.params.teamSlug, filingId: to.params.filingId }
      })
    }
  } catch (error) {
    console.error('Error fetching filing data:', error)
    return next({
      name: 'FilingsDetail',
      params: { teamSlug: to.params.teamSlug, filingId: to.params.filingId }
    })
  }
}

const routes = [
  unauthenticatedCartRoutes,
  // Customer Portal
  {
    path: '/a/:teamSlug/filings/',
    name: 'Portal',
    component: () => import(/* webpackChunkName: "customer-portal" */ '../views/CustomerPortal.vue'),
    props: route => ({ teamSlug: route.params.teamSlug }),
    children: [
      {
        path: '',
        name: 'FilingList',
        component: () => import(/* webpackChunkName: "filing-list" */ '../components/CustomerPortal/FilingListView.vue'),
        props: true
      },
      {
        path: 'add-filing/',
        name: 'addFiling',
        component: () => import(/* webpackChunkName: "add-filing" */ '../components/CustomerPortal/FilingAddFilingForm.vue'),
        props: true
      },
      {
        path: ':filingId',
        name: 'FilingsDetail',
        component: () => import(/* webpackChunkName: "filings-detail" */ '../components/CustomerPortal/FilingsDetail.vue'),
        props: route => ({ teamSlug: route.params.teamSlug, filingId: route.params.filingId }),
        beforeEnter: requireEditableFiling
      },
      {
        path: ':filingId/add-owner',
        name: 'AddOwner',
        component: () => import(/* webpackChunkName: "owner-form" */ '../components/CustomerPortal/BeneficialOwnerForm.vue'),
        props: true,
        beforeEnter: requireEditableFiling
      },
      {
        path: ':filingId/edit-owner/:ownerId',
        name: 'EditOwner',
        component: () => import(/* webpackChunkName: "owner-form" */ '../components/CustomerPortal/BeneficialOwnerForm.vue'),
        props: route => ({ ...route.params, isEditing: true }),
        beforeEnter: requireEditableFiling
      },
      {
        path: ':filingId/add-company-applicant',
        name: 'AddCompanyApplicant',
        component: () => import(/* webpackChunkName: "applicant-form" */ '../components/CustomerPortal/CompanyApplicantForm.vue'),
        props: true,
        beforeEnter: requireEditableFiling
      },
      {
        path: ':filingId/edit-company-applicant/:companyApplicantId',
        name: 'EditCompanyApplicant',
        component: () => import(/* webpackChunkName: "applicant-form" */ '../components/CustomerPortal/CompanyApplicantForm.vue'),
        props: route => ({ ...route.params, isEditing: true }),
        beforeEnter: requireEditableFiling
      },
      {
        path: ':filingId/edit-address',
        name: 'EditAddress',
        component: () => import(/* webpackChunkName: "address-form" */ '../components/CustomerPortal/BusinessAddressForm.vue'),
        props: route => ({ ...route.params, isEditing: true }),
        beforeEnter: requireEditableFiling
      },
      {
        path: ':filingId/edit-company-info',
        name: 'EditCompanyInfo',
        component: () => import(/* webpackChunkName: "company-info-form" */ '../components/CustomerPortal/CompanyInfoForm.vue'),
        props: true,
        beforeEnter: requireEditableFiling
      },
      authenticatedCartRoutes,
    ]
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    return { top: 0 }
  }
})

export default router